import { doc, setDoc, deleteDoc } from 'firebase/firestore'
import { firebaseDb, firebaseStorage } from '@/firebase/init'
import { ref, deleteObject } from 'firebase/storage'
import imageCompression from 'browser-image-compression'
import { notify } from '@/utils/index'

export async function createUploadSession(uniqueId: string) {
  await setDoc(doc(firebaseDb, 'uploads', uniqueId), {
    status: '',
    imageUrl: '',
    timestamp: Date.now(),
  })
}

export const deleteImageSession = async (sessionId: string) => {
  try {
    // Delete the file from Firebase Storage
    const fileRef = ref(firebaseStorage, `uploads/${sessionId}.webp`)
    await deleteObject(fileRef)

    // Delete the document from Firestore
    await deleteDoc(doc(firebaseDb, 'uploads', sessionId))
  } catch (err) {
    console.error('Error deleting document or file: ', err)
    throw err
  }
}

export const handleImageConversion = async (file: File): Promise<File> => {
  // Check if we are on the client side
  if (typeof window !== 'undefined') {
    // Fix for HEIC files where type might be empty
    if (!file.type && file.name.split('.').pop()?.toLowerCase() === 'heic') {
      file = new File([file], file.name, { type: 'image/heic' })
    }

    // Convert HEIC files to JPEG first
    if (file.type === 'image/heic') {
      try {
        // Dynamically import heic2any only when needed
        const heic2any = (await import('heic2any')).default
        const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg' }) as Blob
        file = new File([convertedBlob], `${file.name.split('.')[0]}.jpg`, { type: 'image/jpeg' })
      } catch (error) {
        notify('error', 'HEIC conversion failed. Please try again.')
        console.error('HEIC conversion failed:', error)
        return file
      }
    }

    // Convert file to WEBP
    try {
      const options = {
        maxSizeMB: 10,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/webp',
        initialQuality: 0.75,
      }
      const compressedFile = await imageCompression(file, options)

      // Create a new File object with the .webp extension
      const webpFile = new File([compressedFile], `${file.name.split('.')[0]}.webp`, { type: 'image/webp' })

      return webpFile
    } catch (error) {
      notify('error', 'Image upload failed. Please try again.')
      console.error('Image conversion failed:', error)
    }
  }

  return file
}
